import { useState, useEffect, useCallback } from "react";

const API = window.location.origin;
const api = {
  headers: () => ({ "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("mise_token")}` }),
  async fetch(path, opts = {}) {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...api.headers(), ...opts.headers } });
    if (res.status === 401) { sessionStorage.clear(); window.location.reload(); return null; }
    return res.json();
  },
  get: (p) => api.fetch(p),
  post: (p, b) => api.fetch(p, { method: "POST", body: JSON.stringify(b) }),
  put: (p, b) => api.fetch(p, { method: "PUT", body: JSON.stringify(b) }),
  del: (p) => api.fetch(p, { method: "DELETE" }),
};

const C = {
  bg: "#FAF7F2", surface: "#FFFFFF", surfaceAlt: "#F5F0E8", card: "#FFFFFF",
  border: "#E8E0D4", borderLight: "#F0EBE3", text: "#1A1A1A", textMid: "#5C564E",
  textMuted: "#9A9389", accent: "#E8593C", accentSoft: "rgba(232,89,60,0.08)",
  gold: "#D4920A", goldSoft: "rgba(212,146,10,0.08)", green: "#2D8C3C",
  greenSoft: "rgba(45,140,60,0.08)", blue: "#2563EB", blueSoft: "rgba(37,99,235,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
};

const MESA_STATUS = {
  livre: { label: "Livre", color: C.green, bg: C.greenSoft, border: C.green + "40" },
  ocupada: { label: "Ocupada", color: C.accent, bg: C.accentSoft, border: C.accent },
};

const PAYMENT_LABELS = { dinheiro: "💵 Dinheiro", cartao: "💳 Cartão", pix: "📱 PIX" };

// ============================================
// MESAS TAB — NEUROTECH-X MISE
// ============================================
export default function MesasTab() {
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [mesaOrder, setMesaOrder] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showDividir, setShowDividir] = useState(false);
  const [showPagar, setShowPagar] = useState(false);
  const [pagarTarget, setPagarTarget] = useState(null); // null=mesa inteira, id=pagamento individual
  const [bulkQtd, setBulkQtd] = useState("");
  const [editMesa, setEditMesa] = useState(null);
  const [editForm, setEditForm] = useState({ numero: "", capacidade: "" });
  const [actionLoading, setActionLoading] = useState(false);
  const [contaData, setContaData] = useState(null);

  const user = (() => { try { return JSON.parse(sessionStorage.getItem("mise_user")); } catch { return null; } })();
  const isOwner = user?.role === "owner";

  // ============================================
  // LOAD DATA
  // ============================================
  const loadMesas = useCallback(async () => {
    const data = await api.get("/api/mesas");
    if (data && !data.error) setMesas(data);
    setLoading(false);
  }, []);

  const loadMenuItems = useCallback(async () => {
    const data = await api.get("/api/menu/items");
    if (data) setMenuItems(data.filter(i => i.is_active));
  }, []);

  const loadMesaOrder = useCallback(async (mesaId) => {
    const data = await api.get(`/api/mesas/${mesaId}/pedido`);
    setMesaOrder(data);
  }, []);

  const loadConta = useCallback(async (orderId) => {
    const data = await api.get(`/api/orders/${orderId}/conta`);
    if (data) setContaData(data);
  }, []);

  useEffect(() => { loadMesas(); loadMenuItems(); }, [loadMesas, loadMenuItems]);

  useEffect(() => {
    if (selectedMesa) loadMesaOrder(selectedMesa.id);
    else setMesaOrder(null);
  }, [selectedMesa, loadMesaOrder]);

  // ============================================
  // ACTIONS
  // ============================================
  const abrirMesa = async (mesa) => {
    setActionLoading(true);
    await api.post(`/api/mesas/${mesa.id}/abrir`, {});
    await loadMesas();
    setSelectedMesa({ ...mesa, status: "ocupada" });
    await loadMesaOrder(mesa.id);
    setActionLoading(false);
  };

  const addItems = async (items) => {
    if (!mesaOrder) return;
    setActionLoading(true);
    await api.post(`/api/orders/${mesaOrder.id}/add-items`, { items });
    await loadMesaOrder(selectedMesa.id);
    setShowAddItem(false);
    setActionLoading(false);
  };

  const pagarMesaInteira = async (method) => {
    if (!mesaOrder) return;
    setActionLoading(true);
    await api.post(`/api/orders/${mesaOrder.id}/pay-mesa`, { payment_method: method });
    await loadMesas();
    setSelectedMesa(null);
    setMesaOrder(null);
    setShowPagar(false);
    setActionLoading(false);
  };

  const pagarDivisao = async (pagId, method) => {
    setActionLoading(true);
    await api.post(`/api/pagamentos-mesa/${pagId}/pay`, { payment_method: method });
    if (mesaOrder) await loadConta(mesaOrder.id);
    await loadMesas();
    // Recheck if mesa was freed
    const updatedOrder = await api.get(`/api/mesas/${selectedMesa.id}/pedido`);
    if (!updatedOrder) { setSelectedMesa(null); setMesaOrder(null); setContaData(null); }
    else { setMesaOrder(updatedOrder); }
    setShowPagar(false);
    setPagarTarget(null);
    setActionLoading(false);
  };

  const dividirConta = async (divisoes) => {
    if (!mesaOrder) return;
    setActionLoading(true);
    await api.post(`/api/orders/${mesaOrder.id}/dividir`, { divisoes });
    await loadConta(mesaOrder.id);
    setShowDividir(false);
    setActionLoading(false);
  };

  // ============================================
  // CONFIG: ADD/EDIT/DELETE MESAS
  // ============================================
  const addBulk = async () => {
    const qtd = parseInt(bulkQtd);
    if (!qtd || qtd < 1) return;
    setActionLoading(true);
    await api.post("/api/mesas/bulk", { quantidade: qtd });
    await loadMesas();
    setBulkQtd("");
    setActionLoading(false);
  };

  const saveMesaEdit = async () => {
    if (!editMesa) return;
    setActionLoading(true);
    await api.put(`/api/mesas/${editMesa}`, {
      numero: parseInt(editForm.numero) || undefined,
      capacidade: parseInt(editForm.capacidade) || 0,
    });
    await loadMesas();
    setEditMesa(null);
    setActionLoading(false);
  };

  const deleteMesa = async (id) => {
    if (!window.confirm("Excluir esta mesa?")) return;
    setActionLoading(true);
    const res = await api.del(`/api/mesas/${id}`);
    if (res?.error) alert(res.error);
    await loadMesas();
    setActionLoading(false);
  };

  // ============================================
  // HELPER
  // ============================================
  const timeSince = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return "agora";
    if (diff < 60) return `${diff}min`;
    return `${Math.floor(diff / 60)}h${diff % 60}min`;
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    background: C.bg, color: C.text, fontSize: 13, fontFamily: "'DM Sans',sans-serif",
    outline: "none", boxSizing: "border-box",
  };

  const btnPrimary = {
    padding: "10px 20px", borderRadius: 8, border: "none", background: C.accent,
    color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
    boxShadow: `0 2px 8px ${C.accent}40`,
  };

  const btnSecondary = {
    padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`,
    background: C.surface, color: C.textMid, fontSize: 13, cursor: "pointer",
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: C.textMuted }}>Carregando mesas...</div>;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={{ padding: 28, display: "flex", gap: 24, height: "100%", overflow: "hidden" }}>
      {/* LEFT: Grid de mesas */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Mesas</h2>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
              {mesas.filter(m => m.status === "ocupada").length} ocupada{mesas.filter(m => m.status === "ocupada").length !== 1 ? "s" : ""} · {mesas.filter(m => m.status === "livre").length} livre{mesas.filter(m => m.status === "livre").length !== 1 ? "s" : ""}
            </p>
          </div>
          {isOwner && (
            <button onClick={() => setShowConfig(!showConfig)} style={btnSecondary}>
              ⚙️ Configurar
            </button>
          )}
        </div>

        {/* Config panel (owner only) */}
        {showConfig && isOwner && (
          <div style={{ background: C.card, border: `1px solid ${C.accent}`, borderRadius: 14, padding: 20, marginBottom: 20, boxShadow: `0 4px 20px ${C.accent}15` }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>Configurar Mesas</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
              <input type="number" value={bulkQtd} onChange={e => setBulkQtd(e.target.value)}
                placeholder="Quantidade de mesas" min="1"
                onKeyDown={e => e.key === "Enter" && addBulk()}
                style={{ ...inputStyle, width: 200 }} />
              <button onClick={addBulk} disabled={actionLoading} style={btnPrimary}>
                + Adicionar Mesas
              </button>
            </div>
            {/* List for edit/delete */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
              {mesas.map(m => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: C.surfaceAlt, border: `1px solid ${C.borderLight}` }}>
                  {editMesa === m.id ? (
                    <div style={{ display: "flex", gap: 4, alignItems: "center", flex: 1 }}>
                      <input value={editForm.numero} onChange={e => setEditForm({...editForm, numero: e.target.value})} placeholder="Nº" style={{ ...inputStyle, width: 50, padding: "4px 6px", fontSize: 12 }} />
                      <input value={editForm.capacidade} onChange={e => setEditForm({...editForm, capacidade: e.target.value})} placeholder="Cap." style={{ ...inputStyle, width: 50, padding: "4px 6px", fontSize: 12 }} />
                      <button onClick={saveMesaEdit} style={{ padding: "4px 8px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontSize: 11, cursor: "pointer" }}>✓</button>
                      <button onClick={() => setEditMesa(null)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 11, cursor: "pointer" }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Mesa {m.numero}</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => { setEditMesa(m.id); setEditForm({ numero: m.numero, capacidade: m.capacidade || "" }); }}
                          style={{ padding: "3px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: "transparent", color: C.blue, fontSize: 10, cursor: "pointer" }}>✏️</button>
                        <button onClick={() => deleteMesa(m.id)}
                          style={{ padding: "3px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: "transparent", color: C.red, fontSize: 10, cursor: "pointer" }}>✕</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mesa grid */}
        {mesas.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: C.textMuted }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🍽️</div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>Nenhuma mesa configurada</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Use "Configurar" para adicionar mesas</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
            {mesas.map(m => {
              const st = MESA_STATUS[m.status] || MESA_STATUS.livre;
              const isSelected = selectedMesa?.id === m.id;
              return (
                <div key={m.id} onClick={() => setSelectedMesa(m)} style={{
                  background: isSelected ? st.bg : C.card,
                  border: `2px solid ${isSelected ? st.color : st.border}`,
                  borderRadius: 14, padding: "20px 16px", cursor: "pointer",
                  textAlign: "center", transition: "all 0.2s",
                  boxShadow: isSelected ? `0 4px 16px ${st.color}25` : "0 1px 4px rgba(0,0,0,0.04)",
                  position: "relative",
                }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: st.color, fontFamily: "'Playfair Display', serif" }}>{m.numero}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: st.color, marginTop: 6, padding: "3px 10px", borderRadius: 10, background: st.bg, display: "inline-block" }}>
                    {st.label}
                  </div>
                  {m.capacidade > 0 && (
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6 }}>👥 {m.capacidade} lugares</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RIGHT: Painel da mesa selecionada */}
      {selectedMesa && (
        <div style={{ width: 400, flexShrink: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "20px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: "'Playfair Display', serif" }}>Mesa {selectedMesa.numero}</div>
              <div style={{ fontSize: 12, color: MESA_STATUS[selectedMesa.status]?.color || C.textMuted, fontWeight: 600, marginTop: 2 }}>
                {MESA_STATUS[selectedMesa.status]?.label || selectedMesa.status}
              </div>
            </div>
            <button onClick={() => { setSelectedMesa(null); setMesaOrder(null); setContaData(null); setShowDividir(false); setShowPagar(false); }} style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 14, cursor: "pointer" }}>✕</button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: "auto", padding: "16px 22px" }}>
            {/* Mesa livre — botão abrir */}
            {selectedMesa.status === "livre" && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🍽️</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 20 }}>Mesa disponível</div>
                <button onClick={() => abrirMesa(selectedMesa)} disabled={actionLoading} style={btnPrimary}>
                  {actionLoading ? "Abrindo..." : "Abrir Mesa"}
                </button>
              </div>
            )}

            {/* Mesa ocupada — comanda */}
            {selectedMesa.status === "ocupada" && mesaOrder && (
              <>
                {/* Order info */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: C.textMuted }}>
                    Pedido #{mesaOrder.order_number} · {timeSince(mesaOrder.created_at)}
                  </div>
                </div>

                {/* Items */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Comanda</div>
                  {(!mesaOrder.items || mesaOrder.items.length === 0) ? (
                    <div style={{ padding: 20, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Nenhum item ainda</div>
                  ) : (
                    Object.values((mesaOrder.items || []).filter(it => it && it.id).reduce((acc, item) => {
                      const key = item.name;
                      if (!acc[key]) acc[key] = { name: item.name, quantity: 0, unit_price: Number(item.unit_price), notes: item.notes };
                      acc[key].quantity += Number(item.quantity);
                      return acc;
                    }, {})).map((item, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.borderLight}` }}>
                        <div>
                          <span style={{ fontWeight: 600, color: C.accent, marginRight: 6 }}>{item.quantity}x</span>
                          <span style={{ color: C.text, fontSize: 13 }}>{item.name}</span>
                          {item.notes && <div style={{ fontSize: 11, color: C.gold, marginTop: 2 }}>📝 {item.notes}</div>}
                        </div>
                        <span style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>R$ {(item.quantity * item.unit_price).toFixed(2)}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Total */}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderTop: `2px solid ${C.border}`, marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Total</span>
                  <span style={{ fontSize: 20, fontWeight: 700, color: C.accent, fontFamily: "'Playfair Display', serif" }}>R$ {Number(mesaOrder.total || 0).toFixed(2)}</span>
                </div>

                {/* Conta dividida (se houver) */}
                {contaData && contaData.pagamentos && contaData.pagamentos.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Contas Divididas</div>
                    {contaData.pagamentos.map(p => (
                      <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 8, background: p.payment_status === "pago" ? C.greenSoft : C.surfaceAlt, border: `1px solid ${p.payment_status === "pago" ? C.green + "30" : C.borderLight}`, marginBottom: 6 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{p.descricao}</div>
                          <div style={{ fontSize: 12, color: p.payment_status === "pago" ? C.green : C.gold, fontWeight: 600 }}>
                            {p.payment_status === "pago" ? `✅ Pago · ${PAYMENT_LABELS[p.payment_method] || p.payment_method}` : "⏳ Pendente"}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>R$ {Number(p.valor_total).toFixed(2)}</span>
                          {p.payment_status !== "pago" && (
                            <button onClick={() => { setPagarTarget(p.id); setShowPagar(true); }} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>💰 Pagar</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(!mesaOrder.items || mesaOrder.items.length === 0) && (
                    <button onClick={async () => {
                      if (!window.confirm("Liberar a Mesa " + selectedMesa.numero + "? A comanda vazia sera cancelada.")) return;
                      const res = await api.post("/api/mesas/" + selectedMesa.id + "/liberar", {});
                      if (res && res.error) { alert(res.error); return; }
                      setSelectedMesa(null); setMesaOrder(null); loadMesas();
                    }} style={{ width: "100%", textAlign: "center", padding: "10px 16px", borderRadius: 8, border: "1px solid " + C.border, background: C.surface, color: C.accent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      🔓 Liberar Mesa
                    </button>
                  )}
                  <button onClick={() => setShowAddItem(true)} style={{ ...btnPrimary, width: "100%", textAlign: "center" }}>
                    + Adicionar Item
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={async () => { await loadConta(mesaOrder.id); setShowDividir(true); }} style={{ ...btnSecondary, flex: 1, textAlign: "center" }}>
                      ✂️ Dividir Conta
                    </button>
                    <button onClick={() => { setPagarTarget(null); setShowPagar(true); }} style={{ flex: 1, textAlign: "center", padding: "10px 16px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      💰 Pagar Tudo
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ============================================ */}
          {/* MODAL: Adicionar Item */}
          {/* ============================================ */}
          {showAddItem && <AddItemModal menuItems={menuItems} onAdd={addItems} onClose={() => setShowAddItem(false)} loading={actionLoading} />}

          {/* ============================================ */}
          {/* MODAL: Pagar */}
          {/* ============================================ */}
          {showPagar && (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => setShowPagar(false)}>
              <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 28, width: 340, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 20, fontFamily: "'Playfair Display',serif" }}>
                  {pagarTarget ? "Pagar Conta" : "Pagar Mesa Inteira"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {["dinheiro", "cartao", "pix"].map(m => (
                    <button key={m} onClick={() => pagarTarget ? pagarDivisao(pagarTarget, m) : pagarMesaInteira(m)} disabled={actionLoading} style={{
                      padding: "14px 20px", borderRadius: 10, border: `1px solid ${C.border}`,
                      background: C.surface, color: C.text, fontSize: 14, fontWeight: 500,
                      cursor: "pointer", textAlign: "left", opacity: actionLoading ? 0.6 : 1,
                    }}>
                      {PAYMENT_LABELS[m]}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowPagar(false)} style={{ ...btnSecondary, width: "100%", marginTop: 12, textAlign: "center" }}>Cancelar</button>
              </div>
            </div>
          )}

          {/* ============================================ */}
          {/* MODAL: Dividir Conta */}
          {/* ============================================ */}
          {showDividir && contaData && <DividirContaModal contaData={contaData} onDividir={dividirConta} onClose={() => setShowDividir(false)} loading={actionLoading} />}
        </div>
      )}
    </div>
  );
}

// ============================================
// ADD ITEM MODAL
// ============================================
function AddItemModal({ menuItems, onAdd, onClose, loading }) {
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");

  const filtered = menuItems.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.menu_item_id === item.id);
      if (existing) return prev.map(c => c.menu_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menu_item_id: item.id, name: item.name, unit_price: item.price, quantity: 1 }];
    });
  };

  const removeFromCart = (menuItemId) => {
    setCart(prev => prev.filter(c => c.menu_item_id !== menuItemId));
  };

  const updateQty = (menuItemId, qty) => {
    if (qty < 1) { removeFromCart(menuItemId); return; }
    setCart(prev => prev.map(c => c.menu_item_id === menuItemId ? { ...c, quantity: qty } : c));
  };

  const total = cart.reduce((s, c) => s + c.quantity * c.unit_price, 0);

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 500, maxHeight: "80vh", overflow: "auto", border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>Adicionar Itens</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar item..."
          style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box", marginBottom: 12 }} />

        <div style={{ maxHeight: 250, overflow: "auto", marginBottom: 16 }}>
          {filtered.map(item => {
            const inCart = cart.find(c => c.menu_item_id === item.id);
            return (
              <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.borderLight}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{item.name}</div>
                  <div style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>R$ {Number(item.price).toFixed(2)}</div>
                </div>
                {inCart ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button onClick={() => updateQty(item.id, inCart.quantity - 1)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer", fontSize: 14, color: C.text }}>−</button>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text, minWidth: 20, textAlign: "center" }}>{inCart.quantity}</span>
                    <button onClick={() => updateQty(item.id, inCart.quantity + 1)} style={{ width: 28, height: 28, borderRadius: 6, border: "none", background: C.accent, color: "#fff", cursor: "pointer", fontSize: 14 }}>+</button>
                  </div>
                ) : (
                  <button onClick={() => addToCart(item)} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: C.accentSoft, color: C.accent, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Adicionar</button>
                )}
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ background: C.surfaceAlt, borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Itens selecionados</div>
            {cart.map(c => (
              <div key={c.menu_item_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                <span style={{ color: C.text }}>{c.quantity}x {c.name}</span>
                <span style={{ fontWeight: 600, color: C.text }}>R$ {(c.quantity * c.unit_price).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8, fontWeight: 700, color: C.accent }}>
              <span>Total</span><span>R$ {total.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={() => onAdd(cart)} disabled={cart.length === 0 || loading} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: cart.length === 0 ? C.border : C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: cart.length === 0 ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Adicionando..." : `Confirmar (R$ ${total.toFixed(2)})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// DIVIDIR CONTA MODAL
// ============================================
function DividirContaModal({ contaData, onDividir, onClose, loading }) {
  const [pessoas, setPessoas] = useState([{ nome: "Pessoa 1", itemIds: [] }]);
  const items = (contaData.items || []).filter(i => !i.pagamento_mesa_id);

  const addPessoa = () => setPessoas(prev => [...prev, { nome: `Pessoa ${prev.length + 1}`, itemIds: [] }]);

  const toggleItem = (pessoaIdx, itemId) => {
    setPessoas(prev => prev.map((p, i) => {
      if (i !== pessoaIdx) {
        // Remove from other pessoas
        return { ...p, itemIds: p.itemIds.filter(id => id !== itemId) };
      }
      // Toggle on current
      return { ...p, itemIds: p.itemIds.includes(itemId) ? p.itemIds.filter(id => id !== itemId) : [...p.itemIds, itemId] };
    }));
  };

  const getPersonTotal = (p) => {
    return items.filter(i => p.itemIds.includes(i.id)).reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0);
  };

  const allAssigned = items.every(i => pessoas.some(p => p.itemIds.includes(i.id)));

  const handleConfirm = () => {
    const divisoes = pessoas.filter(p => p.itemIds.length > 0).map(p => ({
      descricao: p.nome,
      item_ids: p.itemIds,
    }));
    if (divisoes.length === 0) return;
    onDividir(divisoes);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 24, width: 600, maxHeight: "85vh", overflow: "auto", border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6, fontFamily: "'Playfair Display',serif" }}>Dividir Conta por Itens</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>Selecione os itens que cada pessoa consumiu</div>

        {items.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: C.textMuted }}>Todos os itens já foram atribuídos a contas</div>
        ) : (
          <>
            {/* Pessoas */}
            {pessoas.map((p, pi) => (
              <div key={pi} style={{ background: C.surfaceAlt, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <input value={p.nome} onChange={e => setPessoas(prev => prev.map((pp, i) => i === pi ? { ...pp, nome: e.target.value } : pp))}
                    style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, fontWeight: 600, color: C.text, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>R$ {getPersonTotal(p).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {items.map(item => {
                    const isSelected = p.itemIds.includes(item.id);
                    const isOther = !isSelected && pessoas.some((pp, i) => i !== pi && pp.itemIds.includes(item.id));
                    return (
                      <button key={item.id} onClick={() => toggleItem(pi, item.id)} style={{
                        padding: "6px 12px", borderRadius: 8,
                        border: `1px solid ${isSelected ? C.accent : isOther ? C.border : C.border}`,
                        background: isSelected ? C.accentSoft : isOther ? C.surfaceAlt : "#fff",
                        color: isSelected ? C.accent : isOther ? C.textMuted : C.text,
                        fontSize: 12, cursor: "pointer", opacity: isOther ? 0.5 : 1,
                        fontFamily: "'DM Sans',sans-serif",
                      }}>
                        {item.quantity}x {item.name} · R${(Number(item.quantity) * Number(item.unit_price)).toFixed(2)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <button onClick={addPessoa} style={{ padding: "8px 16px", borderRadius: 8, border: `1px dashed ${C.border}`, background: "transparent", color: C.textMid, fontSize: 13, cursor: "pointer", width: "100%", marginBottom: 14 }}>
              + Adicionar Pessoa
            </button>
          </>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
          <button onClick={handleConfirm} disabled={!allAssigned || loading || items.length === 0} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: allAssigned && items.length > 0 ? C.accent : C.border, color: "#fff", fontSize: 13, fontWeight: 600, cursor: allAssigned ? "pointer" : "not-allowed", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Dividindo..." : "Confirmar Divisão"}
          </button>
        </div>
        {!allAssigned && items.length > 0 && (
          <div style={{ fontSize: 11, color: C.gold, textAlign: "center", marginTop: 8 }}>⚠️ Atribua todos os itens antes de confirmar</div>
        )}
      </div>
    </div>
  );
}
